/**
 * Componente WelcomeCard - Card de boas-vindas e próximos passos
 * Exibido no dashboard após primeiro acesso ao tenant
 *
 * Redesign Notion-like: estética profissional B2B, sem cara de "template de IA".
 * Mantém integralmente a lógica server-side (RPCs) e regras de progresso.
 */
import { useState, useEffect } from 'react';
import { Progress } from '@/components/ui/progress';
import {
  Inbox,
  Users,
  MessageSquare,
  Workflow,
  Check,
  ChevronRight,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

interface WelcomeCardProps {
  onDismiss: () => void;
}

interface SetupStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  path: string;
  completed: boolean;
}

/**
 * WelcomeCard - Card de boas-vindas com checklist de configuração
 * Mostra progresso de setup e guia usuário pelos passos iniciais
 */
export const WelcomeCard = ({ onDismiss }: WelcomeCardProps) => {
  const navigate = useNavigate();
  const { currentTenant } = useAuth();
  const [isVisible, setIsVisible] = useState(true);

  // Busca dados de setup do tenant para calcular progresso
  // Arquitetura server-side: usa RPCs ao invés de SELECT direto nas tabelas
  const { data: setupData } = useQuery({
    queryKey: ['tenant-setup', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return null;

      // Busca contadores em paralelo via RPCs server-side
      const [inboxesRes, agentsRes, contactsRes, flowsRes] = await Promise.all([
        supabase.rpc('count_active_inboxes', { p_tenant_id: currentTenant.id }),
        supabase.rpc('count_tenant_active_members', { p_tenant_hint: currentTenant.id }),
        supabase.rpc('get_tenant_contact_stats', { p_tenant_hint: currentTenant.id }),
        supabase.rpc('list_tenant_flows', { p_tenant_hint: currentTenant.id }),
      ]);

      const contactsRow = Array.isArray(contactsRes.data) ? contactsRes.data[0] : null;
      const totalContacts = Number(contactsRow?.total_count ?? 0);
      const totalFlows = Array.isArray(flowsRes.data) ? flowsRes.data.length : 0;

      return {
        hasInboxes: Number(inboxesRes.data ?? 0) > 0,
        hasAgents: Number(agentsRes.data ?? 0) > 1, // Mais de 1 porque o owner conta
        hasContacts: totalContacts > 0,
        hasFlows: totalFlows > 0,
      };
    },
    enabled: !!currentTenant?.id,
  });

  // Define passos de configuração com status de completude
  const steps: SetupStep[] = [
    {
      id: 'inbox',
      title: 'Conectar canal',
      description: 'Configure seu WhatsApp ou outro canal de comunicação.',
      icon: Inbox,
      path: '/tenant/inboxes',
      completed: setupData?.hasInboxes || false,
    },
    {
      id: 'agents',
      title: 'Convidar agentes',
      description: 'Adicione os membros da equipe para começar a atender.',
      icon: Users,
      path: '/tenant/agents',
      completed: setupData?.hasAgents || false,
    },
    {
      id: 'contacts',
      title: 'Importar contatos',
      description: 'Traga sua base de clientes via CSV ou integração direta.',
      icon: MessageSquare,
      path: '/tenant/lists',
      completed: setupData?.hasContacts || false,
    },
    {
      id: 'flows',
      title: 'Criar automação',
      description: 'Configure fluxos automáticos para agilizar o atendimento.',
      icon: Workflow,
      path: '/tenant/flows',
      completed: setupData?.hasFlows || false,
    },
  ];

  // Calcula progresso
  const completedSteps = steps.filter(s => s.completed).length;
  const progressPercent = (completedSteps / steps.length) * 100;

  // Animação de entrada
  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(onDismiss, 300);
  };

  const handleStepClick = (path: string) => {
    navigate(path);
  };

  if (!isVisible) return null;

  return (
    <div className="relative overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-all duration-300">
      {/* Botão de fechar - canto superior direito */}
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        aria-label="Fechar"
      >
        <X className="h-4 w-4" />
      </button>

      {/* Header: título + descrição + barra de progresso */}
      <div className="p-4 sm:p-6 border-b border-border">
        <h2 className="text-base sm:text-xl font-semibold text-foreground tracking-tight pr-8">
          Bem-vindo ao UaizyChat
        </h2>
        <p className="mt-1 text-xs sm:text-sm text-muted-foreground">
          Seu workspace{' '}
          <span className="font-medium text-foreground">{currentTenant?.name}</span>{' '}
          está pronto. Complete os passos abaixo para configurar seu ambiente de atendimento.
        </p>

        {/* Bloco de progresso integrado ao header */}
        <div className="mt-5 sm:mt-6 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Configuração inicial
            </span>
            <span className="text-[11px] sm:text-xs font-medium text-foreground">
              {completedSteps} de {steps.length} concluídos
            </span>
          </div>
          <Progress value={progressPercent} className="h-1.5" />
        </div>
      </div>

      {/* Grid de passos com divisores (estilo Notion) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:[&>*:nth-child(2)]:border-l sm:[&>*:nth-child(4)]:border-l [&>*:nth-child(n+3)]:sm:border-t divide-border border-border">
        {steps.map((step) => {
          const Icon = step.completed ? Check : step.icon;
          return (
            <button
              key={step.id}
              type="button"
              onClick={() => handleStepClick(step.path)}
              className="group flex items-start gap-3 sm:gap-4 p-4 sm:p-5 text-left hover:bg-accent/40 transition-colors"
            >
              {/* Ícone em caixinha quadrada arredondada */}
              <div
                className={cn(
                  'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-all',
                  step.completed
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-muted/40 text-muted-foreground group-hover:text-foreground group-hover:border-border'
                )}
              >
                <Icon className="h-4 w-4" strokeWidth={2} />
              </div>

              {/* Título + descrição */}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-foreground truncate">
                  {step.title}
                </h3>
                <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                  {step.description}
                </p>
              </div>

              {/* Chevron com contraste no hover */}
              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground/40 group-hover:text-foreground transition-colors" />
            </button>
          );
        })}
      </div>

      {/* Rodapé sutil com informação de trial */}
      {currentTenant?.trial_ends_at && (
        <div className="flex items-center justify-center bg-muted/40 px-4 py-3 border-t border-border">
          <span className="text-[10px] sm:text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
            Trial até {new Date(currentTenant.trial_ends_at).toLocaleDateString('pt-BR')}
          </span>
        </div>
      )}
    </div>
  );
};
